import { useCallback, useEffect, useRef, useState } from "react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { NfcTagRequestStatus } from "@clipboard-health/contract-backend-main";
import { useSelector, useDispatch } from "react-redux";
import { PageHeader } from "antd";
import { SettingsItem } from "./components";
import { getUserAndWorkplaceSettings, FacilitySetting } from "./services/workplaceSettings";
import {
  addNFCTagRequests,
  createNewNFCTagsRequest,
  updateNFCTagRequestLocation,
  updateNFCTagRequestStatus,
} from "./api/nfcTagRequests";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { FACILITY_ROLES } from "../../constants/facility";
import { Session, SessionType } from "src/modules/interface";
import { useRestrictedSignedProfile } from "src/hooks/useRestrictedSignedProfile";
import { Stack, CircularProgress, Box } from "@mui/material";
import { Title } from "@src/lib/ui-react";
import { ScheduleSettings } from "./components/ScheduleSettings";
import {
  useGetFacilityProfile,
  type BreakPolicyRule,
} from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { isDefined } from "@clipboard-health/util-ts";
import { FacilityPlacementSettings } from "./components/FacilityPlacementSettings";
import { EditContentDialog, useEditContentDialogState } from "./components/EditContentDialog";
import { DocumentationPreferences } from "./components/DocumentationPreferences";
import { useGetWorkplace } from "@src/appV2/FacilityProfile/api/useGetWorkplace";
import { useSupportArticles } from "@src/appV2/HelpCenter/api/useSupportArticles";
import { AttendanceVerificationMethods } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/AttendanceVerificationMethods";
import { NfcTag } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/types";
import { WorkerCredentialConfiguration } from "./components/WorkerCredentialConfiguration";
import { BreakPolicyEditor } from "@src/appV2/redesign/Settings/sections/TimeAttendance/BreakPolicyEditor/BreakPolicyEditor";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { useModalState } from "@src/lib/ui-react";
import { MapleIntegrationConfirmationDialog } from "@src/appV2/redesign/Settings/sections/RateBilling/MapleIntegrationConfirmationDialog";

interface CustomSettingsPageProps {
  userId?: string;
  isPageHeaderVisible?: boolean;
  className?: string;
}
function CustomSettingsPage(props: CustomSettingsPageProps) {
  const ldFlags = useFlags();
  const { isPageHeaderVisible = true, className } = props;

  const session = useSelector<SessionType, SessionType["session"]>((state) => state.session);
  const currentUserID = session?.admin ? session?.adminUserId : session?.userId;
  const dispatch = useDispatch();

  const [isLoadings, setLoadings] = useState({
    getUser: true,
    updatingNfcCheck: false,
    updatingRequiresLunchBreak: false,
    updatingNfcTagsRequests: false,
    updatingAllowEarlyClockIn: false,
    updatingExtraWorkedTimeAutoDeclination: false,
    updatingHostedTimeDeviceId: false,
    updatingRestBreaks: false,
  });
  const [dataSource, setWorkplaceSettings] = useState<FacilitySetting[]>([]);
  const [nfcRequestList, setNFCRequestList] = useState<NfcTag[]>([]);
  const [hasExistingNfcTagRecord, setHasExistingNfcTagRecord] = useState<boolean>(false);
  const [isLDToggleValuesFetched, setIsLDToggleValuesFetched] = useState<boolean>(false);

  const extraWorkedTimeAutoDeclinationReasonModalState = useEditContentDialogState({
    fieldName: "Reason",
    minCharacters: 60,
    maxCharacters: 2000,
  });

  const mandatoryBreakPolicyDialogState = useEditContentDialogState({
    fieldName: "Policy",
    minCharacters: 30,
    maxCharacters: 1500,
  });

  const restBreakPolicyDialogState = useEditContentDialogState({
    fieldName: "Policy",
    minCharacters: 30,
    maxCharacters: 1500,
  });

  const [pendingMapleValue, setPendingMapleValue] = useState<boolean | undefined>(undefined);
  const mapleConfirmationModalState = useModalState();
  const pendingMapleConfirmRef = useRef<(() => Promise<void>) | null>(null);
  const [isMapleToggleLoading, setIsMapleToggleLoading] = useState(false);

  const handleMapleToggleRequested = useCallback(
    (checked: boolean, confirmFn: () => Promise<void>) => {
      setPendingMapleValue(checked);
      pendingMapleConfirmRef.current = confirmFn;
      mapleConfirmationModalState.openModal();
    },
    [mapleConfirmationModalState]
  );

  const facilityId = props.userId ?? session?.profile.userId;

  const {
    data: facilityProfile,
    isSuccess: isGetFacilityProfileSuccess,
    refetch: refetchFacilityProfile,
  } = useGetFacilityProfile(
    facilityId ?? "",
    "maxAllowedWorkConsecutiveHours maxAllowedWorkHoursPerWeek requiresLunchBreak breakPolicies",
    { enabled: isDefined(facilityId) }
  );

  const {
    data: workplace,
    isSuccess: isGetWorkplaceSuccess,
    refetch: refetchWorkplace,
  } = useGetWorkplace({
    workplaceId: facilityId,
  });

  const { isSignedProfile, openContractModal } = useRestrictedSignedProfile();

  const ldClient = useLDClient();
  const { type } = useSelector<{ session: Session }, Session>((state) => state.session);
  const isAdmin = type === "ADMIN";
  const setLDToggleBasedOnMSA = async () => {
    if (!ldClient) {
      return;
    }
    setIsLDToggleValuesFetched(true);
  };
  const { data: supportArticlesData } = useSupportArticles();

  const retrieveFacilityDataAndSetDataSource = useCallback(() => {
    if (session?.userId && ldFlags && isLDToggleValuesFetched) {
      void getUserAndWorkplaceSettings({
        isLoadings,
        setLoadings,
        currentUserID,
        userId: isAdmin ? props.userId : session.userId,
        userAccess: session.user?.access || [],
        isAdmin: session.admin,
        ldFlags,
        // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'items' implicitly has an 'any' type.
        setWorkplaceSettings: (items) =>
          setWorkplaceSettings(
            // @ts-expect-error TODO(typecheck-unify): TS7006 Parameter 'item' implicitly has an 'any' type.
            items.map((item) => ({
              ...item,
              ...(isSignedProfile
                ? {}
                : {
                    onClick: () =>
                      openContractModal({
                        triggeredBy: "update settings",
                        triggeredFrom: "Settings",
                      }),
                    onChange: () =>
                      openContractModal({
                        triggeredBy: "update settings",
                        triggeredFrom: "Settings",
                      }),
                  }),
            }))
          ),
        setNFCRequestList,
        setHasExistingNfcTagRecord,
        extraWorkedTimeAutoDeclinationReasonModalState,
        mandatoryBreakPolicyDialogState,
        restBreakPolicyDialogState,
        supportArticlesData,
        dispatch,
        onMapleToggleRequested: handleMapleToggleRequested,
      });
    }
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLDToggleValuesFetched, supportArticlesData]);

  useEffect(() => {
    void setLDToggleBasedOnMSA();
    retrieveFacilityDataAndSetDataSource();
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLDToggleValuesFetched, facilityProfile, retrieveFacilityDataAndSetDataSource]);

  const isFacilityAdminOrCBHAdmin =
    session?.user?.access?.includes(FACILITY_ROLES.ADMIN) || session?.admin;
  const isCbhAdmin = session?.admin;

  const isLoading = Object.values(isLoadings).some((value) => value);
  const workplaceId = workplace?.data?.id;

  const { mutateAsync: updateFacilityProfile, isLoading: isUpdatingFacilityProfile } =
    useUpdateFacilityProfile();

  const handleBreakPolicySave = async (rules: BreakPolicyRule[]): Promise<boolean> => {
    if (!facilityId) {
      return false;
    }
    try {
      await updateFacilityProfile({ facilityId, breakPolicies: rules });
      showSuccessToast("Mandatory break rules updated successfully.");
      void refetchFacilityProfile();
      return true;
    } catch {
      showErrorToast("Failed to update mandatory break rules.");
      return false;
    }
  };

  return (
    <>
      <div className={className}>
        {isPageHeaderVisible && <PageHeader title="Settings" />}
        <Stack gap={4}>
          <Stack position="relative" pl={2} sx={{ opacity: isLoading ? 0.5 : 1 }}>
            <Title component="h3" sx={{ marginTop: 2 }}>
              Time-Clock Settings
            </Title>
            {isLoading ? (
              <Box position="absolute" top="calc(50% - 24px)" left="calc(50% - 24px)">
                <CircularProgress size={48} />
              </Box>
            ) : null}
            {isFacilityAdminOrCBHAdmin && (
              <>
                {dataSource.map((item) => {
                  if (item.key === "mandatoryBreakPolicy") {
                    return null;
                  }

                  if (item.key === "hcfLunchBreak") {
                    const policyRow = dataSource.find(
                      (entry) => entry.key === "mandatoryBreakPolicy"
                    );
                    const showBreakPolicyEditor = isCbhAdmin && item.checked === true;

                    return (
                      <Box
                        key={item.key}
                        sx={{
                          borderBottom: "1px solid #f0f0f0",
                          "& > [role=row]": { borderBottom: "none" },
                        }}
                      >
                        <SettingsItem item={item} isLoading={item.loading || isLoading} />
                        {policyRow && (
                          <SettingsItem
                            item={policyRow}
                            isLoading={policyRow.loading || isLoading}
                          />
                        )}
                        {showBreakPolicyEditor && (
                          <BerlinThemeProvider>
                            <Box sx={{ paddingBottom: 4 }}>
                              <BreakPolicyEditor
                                savedRules={facilityProfile?.breakPolicies}
                                isSaving={isUpdatingFacilityProfile}
                                onSave={handleBreakPolicySave}
                              />
                            </Box>
                          </BerlinThemeProvider>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <SettingsItem
                      key={item.key}
                      item={item}
                      isLoading={item.loading || isLoading}
                    />
                  );
                })}
              </>
            )}
            {!isFacilityAdminOrCBHAdmin && <div>This configuration only visible to Admin</div>}
            {isFacilityAdminOrCBHAdmin && workplaceId && (
              <Box sx={{ marginTop: 2 }}>
                <AttendanceVerificationMethods
                  workplaceId={workplaceId}
                  isCbhAdmin={isCbhAdmin}
                  nfcRequestList={nfcRequestList.map((item) => ({
                    tagId: item.tagId,
                    tagNumber: item.tagNumber,
                    tagStatus: item.tagStatus,
                    tagLocation: item.tagLocation,
                  }))}
                  onSaveNfcTagLocation={async ({ tagId, location, currentStatus }) => {
                    if (currentStatus !== NfcTagRequestStatus.SET_UP) {
                      await updateNFCTagRequestStatus({
                        facilityId: workplaceId,
                        nfcTagRequests: [
                          {
                            tagId,
                            tagStatus: NfcTagRequestStatus.SET_UP,
                            tagLocation: location,
                          },
                        ],
                      });
                    } else {
                      await updateNFCTagRequestLocation({
                        tagId,
                        facilityId: workplaceId,
                        tagLocation: location,
                      });
                    }

                    showSuccessToast("Location updated successfully");
                    retrieveFacilityDataAndSetDataSource();
                  }}
                  onDeleteNfcTag={async (tagId) => {
                    await updateNFCTagRequestStatus({
                      facilityId: workplaceId,
                      nfcTagRequests: [
                        {
                          tagId,
                          tagStatus: NfcTagRequestStatus.DELETED,
                        },
                      ],
                    });
                    showSuccessToast("Tag deleted successfully");
                    retrieveFacilityDataAndSetDataSource();
                  }}
                  onRequestNfcTags={async (numberOfTags) => {
                    if (hasExistingNfcTagRecord) {
                      await addNFCTagRequests(workplaceId, numberOfTags);
                    } else {
                      await createNewNFCTagsRequest(workplaceId, numberOfTags);
                    }
                    showSuccessToast("Tags requested successfully");
                    retrieveFacilityDataAndSetDataSource();
                  }}
                />
              </Box>
            )}
          </Stack>
          {isGetFacilityProfileSuccess && isGetWorkplaceSuccess && (
            <>
              {isCbhAdmin && (
                <WorkerCredentialConfiguration
                  workplace={workplace}
                  onPreferencesChanged={() => {
                    void refetchFacilityProfile();
                    void refetchWorkplace();
                  }}
                />
              )}
              <ScheduleSettings
                facilityProfile={{
                  ...facilityProfile,
                  instantBook: workplace.data.attributes.instantBook,
                  requestToBook: workplace.data.attributes.requestToBook,
                }}
                workplace={workplace}
                onSettingsChanged={() => {
                  void refetchFacilityProfile();
                  void refetchWorkplace();
                }}
              />
              <DocumentationPreferences
                workplace={workplace}
                onPreferencesChanged={() => {
                  void refetchFacilityProfile();
                  void refetchWorkplace();
                }}
              />
              {currentUserID && <FacilityPlacementSettings facilityProfile={facilityProfile} />}
            </>
          )}
        </Stack>
      </div>
      <EditContentDialog
        dialogState={extraWorkedTimeAutoDeclinationReasonModalState}
        dialogTitle="Please enter a default declination reason to use at the end of the review window"
      />
      <EditContentDialog
        dialogState={mandatoryBreakPolicyDialogState}
        dialogTitle="Review and edit your facility's mandatory break policy"
      />
      <EditContentDialog
        dialogState={restBreakPolicyDialogState}
        dialogTitle="Please review and edit the Paid Rest Period Policy"
      />
      <BerlinThemeProvider>
        <MapleIntegrationConfirmationDialog
          modalState={mapleConfirmationModalState}
          isTurningOn={pendingMapleValue ?? false}
          isLoading={isMapleToggleLoading}
          onConfirm={() => {
            const confirmFn = pendingMapleConfirmRef.current;
            pendingMapleConfirmRef.current = null;
            setIsMapleToggleLoading(true);
            void confirmFn?.()?.finally(() => {
              mapleConfirmationModalState.closeModal();
              setPendingMapleValue(undefined);
              setIsMapleToggleLoading(false);
            });
          }}
          onCancel={() => {
            pendingMapleConfirmRef.current = null;
            mapleConfirmationModalState.closeModal();
            setPendingMapleValue(undefined);
          }}
        />
      </BerlinThemeProvider>
    </>
  );
}

export const CustomSettings = CustomSettingsPage;
